import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Phone, Shield, Database, UserCircle, ArrowRight, Activity, Layers } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function ArchitecturePage() {
  return <motion.div initial="hidden" animate="visible" variants={fadeUp} className="w-full max-w-6xl mx-auto" data-id="element-113">
      <div className="text-center mb-12" data-id="element-114">
        <h2 className="text-4xl font-bold text-sierra-dark mb-4" data-id="element-115">
          Enterprise Architecture
        </h2>
        <p className="text-xl text-gray-500" data-id="element-116">
          Secure, composable, and integrated into your existing stack.
        </p>
      </div>

      <div className="bg-white p-10 rounded-3xl sierra-shadow border border-gray-100 relative" data-id="element-117">
        {/* Main Diagram Container */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 relative z-10" data-id="element-118">
          {/* 1. Channels */}
          <div className="flex flex-col gap-3 w-40 shrink-0" data-id="element-119">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider text-center mb-2" data-id="element-120">
              Channels
            </div>
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl flex items-center gap-3 justify-center sierra-shadow" data-id="element-121">
              <MessageSquare className="w-5 h-5 text-sierra-blue" data-id="element-122" />
              <span className="font-semibold text-sm" data-id="element-123">Chat</span>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl flex items-center gap-3 justify-center sierra-shadow" data-id="element-124">
              <Phone className="w-5 h-5 text-sierra-blue" data-id="element-125" />
              <span className="font-semibold text-sm" data-id="element-126">Voice</span>
            </div>
          </div>

          <ArrowRight className="w-6 h-6 text-gray-300 hidden md:block shrink-0" data-id="element-127" />

          {/* 2. Core Sierra */}
          <div className="flex-1 min-w-[300px] bg-gradient-to-b from-green-50 to-white border-2 border-green-100 rounded-2xl p-6 relative sierra-shadow" data-id="element-128">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sierra-blue text-white px-4 py-1 rounded-full text-xs font-bold tracking-wider" data-id="element-129">
              SIERRA PLATFORM
            </div>

            <div className="space-y-4 mt-4" data-id="element-130">
              <div className="bg-white border border-green-100 p-3 rounded-lg flex items-center justify-between" data-id="element-131">
                <span className="text-sm font-semibold text-gray-700" data-id="element-132">
                  Experience Engine
                </span>
                <Shield className="w-4 h-4 text-green-600" data-id="element-133" />
              </div>

              <div className="bg-sierra-blue text-white p-4 rounded-xl text-center shadow-md" data-id="element-134">
                <div className="font-bold text-sm" data-id="element-135">Reasoning Engine</div>
                <p className="text-xs text-green-200 mt-1" data-id="element-136">
                  Goal-oriented problem solving
                </p>
              </div>

              <div className="bg-white border border-emerald-200 p-3 rounded-lg flex items-center justify-between border-l-4 border-l-emerald-500" data-id="element-137">
                <span className="font-semibold text-sm text-sierra-dark" data-id="element-138">
                  Guardrails & Supervisor
                </span>
                <Shield className="w-4 h-4 text-emerald-500" data-id="element-139" />
              </div>
            </div>
          </div>

          <ArrowRight className="w-6 h-6 text-gray-300 hidden md:block shrink-0" data-id="element-140" />

          {/* 3. Integrations & Handoff */}
          <div className="flex flex-col gap-6 w-56 shrink-0" data-id="element-141">
            <div className="space-y-3" data-id="element-142">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider text-center mb-2" data-id="element-143">
                Systems of Record
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded-xl flex items-center gap-3 sierra-shadow" data-id="element-144">
                <Database className="w-4 h-4 text-gray-500" data-id="element-145" />
                <span className="font-semibold text-sm" data-id="element-146">Salesforce (CRM)</span>
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded-xl flex items-center gap-3 sierra-shadow" data-id="element-147">
                <Activity className="w-4 h-4 text-gray-500" data-id="element-148" />
                <span className="font-semibold text-sm" data-id="element-149">ServiceNow (ITSM)</span>
              </div>
              <div className="bg-white border border-gray-200 p-3 rounded-xl flex items-center gap-3 sierra-shadow" data-id="element-150">
                <Layers className="w-4 h-4 text-gray-500" data-id="element-151" />
                <span className="font-semibold text-sm" data-id="element-152">
                  Carrier APIs (eSIM)
                </span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-153">
              <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl flex items-center gap-3 justify-center sierra-shadow" data-id="element-154">
                <UserCircle className="w-5 h-5 text-purple-600" data-id="element-155" />
                <span className="font-semibold text-sm text-purple-900" data-id="element-156">
                  Human Handoff
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Background decorative elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[60%] bg-green-50/50 rounded-full blur-3xl -z-10" data-id="element-157" />
      </div>

      {/* Callouts */}
      <div className="grid grid-cols-3 gap-6 mt-8" data-id="element-158">
        <div className="text-center" data-id="element-159">
          <h4 className="font-semibold text-sm text-sierra-dark mb-1" data-id="element-160">
            Composable Skills
          </h4>
          <p className="text-xs text-gray-500" data-id="element-161">Triage, respond, confirm</p>
        </div>
        <div className="text-center" data-id="element-162">
          <h4 className="font-semibold text-sm text-sierra-dark mb-1" data-id="element-163">
            Simulations
          </h4>
          <p className="text-xs text-gray-500" data-id="element-164">Test safely before release</p>
        </div>
        <div className="text-center" data-id="element-165">
          <h4 className="font-semibold text-sm text-sierra-dark mb-1" data-id="element-166">
            Observability Loop
          </h4>
          <p className="text-xs text-gray-500" data-id="element-167">Improve containment + CSAT</p>
        </div>
      </div>
    </motion.div>;
}